import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { TanTable } from '../../../components/TanTable';
import { config } from '../../../config/config';
import { TurnoverDriver } from './logic';

/**
 * The drivers behind one number on the Turnover table — a dispatcher's opening
 * or closing roster, or the hires or terminations in between.
 *
 * Driver detail is current state: safetynew keeps one row per unit number and
 * overwrites it when a truck changes hands, so for a unit since reassigned the
 * name and dates belong to whoever holds it now. The note under the header says
 * so rather than letting the table imply otherwise.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  rows: TurnoverDriver[];
  /** 'Hired On' / 'Left On'; blank for the roster snapshots, which have no event. */
  eventLabel: string;
}

// 'YYYY-MM-DD' -> 'M/D/YY' (local, no UTC shift).
const fmtDate = (s?: string): string => {
  if (!s) return '-';
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return `${m}/${d}/${String(y).slice(2)}`;
};

const TurnoverDetailModal: React.FC<Props> = ({ open, onClose, title, rows, eventLabel }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const columns = useMemo<ColumnDef<TurnoverDriver>[]>(() => {
    const cols: ColumnDef<TurnoverDriver>[] = [
    {
      accessorKey: 'unit',
      header: 'Driver #',
      filterFn: 'arrIncludes',
      size: 90,
      // Opens the driver in the safety system, same link the Driver Status
      // Monitor and the Drivers page use.
      cell: ({ getValue }) => {
        const unit = getValue<string>();
        const n = parseInt(unit, 10);
        if (!unit || Number.isNaN(n)) return <span className="font-medium">{unit || '-'}</span>;
        return (
          <a
            href={`${config.V2_URL}/safety/drivers/${n}/driver`}
            target="_blank"
            rel="noopener noreferrer"
            title="View in CMS"
            className="font-medium text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
          >
            {unit}
          </a>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      filterFn: 'arrIncludes',
      size: 220,
      cell: ({ getValue }) => getValue<string>() || '-',
    },
    {
      accessorKey: 'preference',
      header: 'Preference',
      filterFn: 'arrIncludes',
      size: 110,
      cell: ({ getValue }) => getValue<string>() || '-',
    },
    {
      accessorKey: 'start_date',
      header: 'Start Date',
      filterFn: 'arrIncludes',
      size: 110,
      cell: ({ getValue }) => fmtDate(getValue<string>()),
    },
    {
      accessorKey: 'in_service_since',
      header: 'In Service',
      filterFn: 'arrIncludes',
      size: 105,
      cell: ({ getValue }) => fmtDate(getValue<string>()),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      filterFn: 'arrIncludes',
      size: 130,
      cell: ({ row, getValue }) => {
        const v = getValue<string>();
        // "(rehired)" comes from the events — this unit was hired back after
        // this termination, inside this period — not from the status, which a
        // rehire erases from cms and which can be stale regardless.
        const tag = row.original.rehired
          ? <span className="ml-1 text-muted">(rehired)</span>
          : null;
        if (!v) return tag ? <span className="text-muted">-{tag}</span> : '-';
        if (v.toUpperCase().startsWith('TERMINAT')) {
          // Terminated reads red, so a list of departures is scannable without
          // reading every row.
          return <span className="font-semibold text-red-700 dark:text-red-300">{v}</span>;
        }
        return <span>{v}{tag}</span>;
      },
    },
    {
      accessorKey: 'term_date',
      header: 'Term Date',
      filterFn: 'arrIncludes',
      size: 110,
      cell: ({ getValue }) => {
        const v = getValue<string>();
        return v
          ? <span className="font-semibold text-red-700 dark:text-red-300">{fmtDate(v)}</span>
          : <span className="text-muted">-</span>;
      },
    },
    ];
    // The date that put this driver in the period being viewed, placed third so
    // it reads without scrolling. It is the whole explanation for a row whose
    // hire date is years earlier, or whose status still says IN SERVICE: the
    // driver left and came back inside the period.
    if (eventLabel) {
      cols.splice(2, 0, {
        accessorKey: 'event_date',
        header: eventLabel,
        filterFn: 'arrIncludes',
        size: 110,
        cell: ({ getValue }) => <span className="font-semibold">{fmtDate(getValue<string>())}</span>,
      });
    }
    return cols;
  }, [eventLabel]);

  useAltHotkeys({ C: onClose }, undefined, { layer: open });
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-scrim/50 p-4" onClick={onClose}>
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative flex flex-col w-full max-w-5xl h-[80vh] max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={title}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          className="flex-shrink-0"
        />
        <div className="px-3 pt-2 text-[0.6875rem] italic text-muted">
          {eventLabel ? `${eventLabel} is the date that put each driver in this period. ` : ''}
          Start Date is the cms hire date, In Service is when the driver's current spell on the
          roster began, and Status is where they stand now. Name and dates are the driver
          currently on each unit number.
        </div>
        <div className="flex-1 min-h-0 p-3">
          <TanTable
            data={rows}
            columns={columns}
            loading={false}
            // A unit hired twice in the period is two rows, so the id carries
            // the event date. The fact table holds one row per unit per day, so
            // a unit cannot fire the same flag twice on one date.
            getRowId={(r) => `${r.unit}|${r.event_date ?? ''}`}
            searchPlaceholder="Search drivers…"
            enableColumnFilters
            enableSearch
            enableExcelExport
            fillContainer
            disableVirtualization
            defaultPageSize={200}
            settingsKey="dispatcher-metrics-turnover-detail"
            disableSettingsPersistence
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TurnoverDetailModal;
